import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  HelperText,
  IconButton,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { PencilIcon, PlusIcon, SearchIcon, TimesIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import { US_STATES, UsStateCode } from '@/enterprise/components/InstantPay/usStates';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/enterprise/components/Table';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { BUSINESS_DETAILS_CONTENT, MANUAL_CUSTOMERS_CONTENT } from './content';
import { NoCustomersAddedState } from './NoCustomersAddedState';
import { QuoteEntity, findQuoteEntities, formatQuoteAddress, QUOTE_ENTITY_CATALOG } from './quoteEntityCatalog';
import {
  AddedCustomer,
  CUSTOMER_COUNTRY,
  US_ZIP_PATTERN,
  customerKey,
  parseMonthlyInvoiceVolume,
} from './quoteWizardState';
import { RemoveCustomerModal } from './RemoveCustomerModal';

interface ManualCustomersStepProps {
  customers: AddedCustomer[];
  onAdd: (customer: AddedCustomer) => void;
  /** Replaces the row whose `customerKey` is `originalKey`, so an edit keeps its place. */
  onUpdate: (originalKey: string, customer: AddedCustomer) => void;
  /** Takes a `customerKey`, not an entity id — a typed customer has no entity id. */
  onRemove: (key: string) => void;
  /** True while an add or edit is open. Unmounting discards that entry, so it reports false then. */
  onOpenEntryChange: (open: boolean) => void;
  /** A failed quote request. The wizard decides when there is one; this step only places it. */
  quoteError?: { title: string; description: string };
}

interface CustomerFormValues {
  name: string;
  street: string;
  city: string;
  state: UsStateCode | '';
  zip: string;
  volume: string;
}

/**
 * Which customer the detail form is for. `idle` means the form is closed. `add` is a new customer,
 * with the catalog match in `entity`, or null when the name was typed. `edit` is a saved row, and
 * `key` is what keeps that row in place.
 */
type CustomerDraft =
  | { kind: 'idle' }
  | { kind: 'add'; entity: QuoteEntity | null }
  | { kind: 'edit'; key: string; entityId: string | null };

const EMPTY_FORM: CustomerFormValues = {
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  volume: '',
};

const valuesFromEntity = (entity: QuoteEntity): CustomerFormValues => ({
  name: entity.name,
  street: entity.street ?? '',
  city: entity.city ?? '',
  state: entity.state ?? '',
  zip: entity.zip ?? '',
  volume: '',
});

const valuesFromCustomer = (customer: AddedCustomer): CustomerFormValues => ({
  name: customer.name,
  street: customer.address.street,
  city: customer.address.city,
  state: customer.address.state,
  zip: customer.address.zip,
  volume: customer.volume,
});

const stateName = (code: string): string => US_STATES.find((state) => state.code === code)?.name ?? code;

/**
 * Catalog parts the supplier must not retype. Recomputed from the catalog on edit, not stored on
 * the row, so a later catalog change is what the form locks.
 */
const locksFor = (entity: QuoteEntity | null) => ({
  name: Boolean(entity),
  street: Boolean(entity?.street),
  city: Boolean(entity?.city),
  state: Boolean(entity?.state),
  zip: Boolean(entity?.zip),
});

/** The list cell. A blank or unparseable volume stays "-"; the stored string is left as typed for edit and the quotes POST. */
const formatSavedVolume = (volume: string, asCurrency: (value: number, currency: string) => string) => {
  const parsed = parseMonthlyInvoiceVolume(volume);
  if (parsed === null || !Number.isFinite(parsed)) return '-';

  return asCurrency(parsed, 'USD');
};

const ReadOnlyValue = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <p className="m-0 p-0 text-base font-medium text-text-primary">{label}</p>
    <p className="m-0 p-0 text-base text-text-secondary">{value}</p>
  </div>
);

export const ManualCustomersStep = ({
  customers,
  onAdd,
  onRemove,
  onUpdate,
  onOpenEntryChange,
  quoteError,
}: ManualCustomersStepProps) => {
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const [customerDraft, setCustomerDraft] = useState<CustomerDraft>({ kind: 'idle' });
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const suggestionsId = useId();
  const { asCurrency } = useLocaleFormat();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CustomerFormValues>({ mode: 'onChange', defaultValues: EMPTY_FORM });
  const watched = watch();

  const activeEntity: QuoteEntity | null =
    customerDraft.kind === 'add'
      ? customerDraft.entity
      : customerDraft.kind === 'edit' && customerDraft.entityId
      ? QUOTE_ENTITY_CATALOG.find((entity) => entity.id === customerDraft.entityId) ?? null
      : null;
  const locks = locksFor(activeEntity);
  const formOpen = customerDraft.kind !== 'idle';
  const editingKey = customerDraft.kind === 'edit' ? customerDraft.key : null;

  useEffect(() => {
    onOpenEntryChange(formOpen);
    return () => onOpenEntryChange(false);
  }, [formOpen, onOpenEntryChange]);

  // The menu follows the search text only. A suggestion or Add opens the detail form; typing,
  // Enter, and leaving the field do not. The add control stays on that list even with no match.
  const suggestions = findQuoteEntities(customerSearch);
  const trimmedSearch = customerSearch.trim();
  const showSearchMenu = searchMenuOpen && trimmedSearch.length > 0;
  const enteredName = locks.name && activeEntity ? activeEntity.name : watched.name;
  const normalizedName = customerKey(enteredName);
  const isDuplicate =
    formOpen &&
    normalizedName.length > 0 &&
    customers.some(
      (customer) => customerKey(customer.name) === normalizedName && customerKey(customer.name) !== editingKey
    );
  // Validity is checked here rather than with `isValid`, because validating on open paints the
  // volume error before the supplier has touched the field. An empty volume stays quiet.
  const canSave =
    formOpen &&
    !isDuplicate &&
    enteredName.trim().length > 0 &&
    (locks.street || watched.street.trim().length > 0) &&
    (locks.city || watched.city.trim().length > 0) &&
    (locks.state || watched.state !== '') &&
    (locks.zip || US_ZIP_PATTERN.test(watched.zip.trim())) &&
    (watched.volume.trim() === '' || Number.isFinite(parseMonthlyInvoiceVolume(watched.volume)));

  const loadForm = (next: CustomerDraft, values: CustomerFormValues) => {
    setCustomerDraft(next);
    reset(values);
  };

  const abandon = () => {
    setCustomerSearch('');
    setCustomerDraft({ kind: 'idle' });
    reset(EMPTY_FORM);
  };

  const commitTypedName = () => {
    const name = customerSearch.trim();
    if (!name) return;
    setCustomerSearch('');
    loadForm({ kind: 'add', entity: null }, { ...EMPTY_FORM, name });
  };

  const handleSelect = (entity: QuoteEntity) => {
    setCustomerSearch('');
    loadForm({ kind: 'add', entity }, valuesFromEntity(entity));
  };

  const startEdit = (customer: AddedCustomer) => {
    setCustomerSearch('');
    loadForm(
      { kind: 'edit', key: customerKey(customer.name), entityId: customer.entityId },
      valuesFromCustomer(customer)
    );
  };

  const saveCustomer = handleSubmit((values) => {
    if (customerDraft.kind === 'idle' || isDuplicate) return;

    const state = locks.state && activeEntity?.state ? activeEntity.state : values.state;
    const volume = values.volume.trim();
    if (state === '' || (volume !== '' && !Number.isFinite(parseMonthlyInvoiceVolume(volume)))) return;

    const name = (locks.name && activeEntity ? activeEntity.name : values.name).trim();
    if (!name) return;

    const customer: AddedCustomer = {
      entityId: activeEntity?.id ?? null,
      name,
      address: {
        street: (locks.street ? activeEntity?.street : values.street) ?? '',
        city: (locks.city ? activeEntity?.city : values.city) ?? '',
        state,
        zip: (locks.zip ? activeEntity?.zip : values.zip) ?? '',
      },
      volume,
    };

    if (customerDraft.kind === 'edit') {
      onUpdate(customerDraft.key, customer);
    } else {
      onAdd(customer);
    }

    abandon();
  });

  const pendingCustomer =
    pendingDeleteKey === null
      ? null
      : customers.find((customer) => customerKey(customer.name) === pendingDeleteKey) ?? null;

  const confirmDelete = () => {
    if (!pendingDeleteKey) return;

    onRemove(pendingDeleteKey);
    if (customerDraft.kind === 'edit' && customerDraft.key === pendingDeleteKey) {
      abandon();
    }

    setPendingDeleteKey(null);
  };

  const zipRules = {
    required: BUSINESS_DETAILS_CONTENT.errors.zipCode,
    pattern: { value: US_ZIP_PATTERN, message: BUSINESS_DETAILS_CONTENT.errors.zipCode },
    setValueAs: (value: string) => value.trim(),
  };

  const showValueProps = customers.length === 0 && !formOpen;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="space-y-6 rounded-xl bg-white p-6 shadow-sm">
        {quoteError && (
          <Alert type="error" variant="outlined" full title={quoteError.title} description={quoteError.description} />
        )}
        <h2 className="m-0 p-0 text-xl font-bold text-text-primary">{MANUAL_CUSTOMERS_CONTENT.heading}</h2>

        <div className="flex flex-col gap-1">
          {/* Liquidity 6.1.2 has no combobox. `SearchInput` copies `value` into internal state at
              mount and never syncs, so a pick could not update it. Its clear control is an end slot
              inside the field; `TextInput` has no button slot, so the same placement is done here. */}
          <div
            className={`relative ${customerSearch.length > 0 ? '[&_input]:pr-11' : ''}`}
            onBlur={(event) => {
              // The clear control and the suggestion list sit in this field. Closing on the
              // input's own blur would dismiss the list before a click inside it lands.
              const next = event.relatedTarget;
              if (next instanceof Node && event.currentTarget.contains(next)) return;
              setSearchMenuOpen(false);
            }}
          >
            <TextInput
              id="customerSearch"
              className="w-full"
              startIcon={SearchIcon}
              aria-label={MANUAL_CUSTOMERS_CONTENT.searchPlaceholder}
              placeholder={MANUAL_CUSTOMERS_CONTENT.searchPlaceholder}
              aria-controls={showSearchMenu ? suggestionsId : undefined}
              aria-expanded={showSearchMenu}
              aria-autocomplete="list"
              autoComplete="off"
              value={customerSearch}
              onFocus={() => setSearchMenuOpen(true)}
              onChange={(event) => {
                setCustomerSearch(event.target.value);
                setSearchMenuOpen(true);
              }}
            />
            {customerSearch.length > 0 && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  aria-label={MANUAL_CUSTOMERS_CONTENT.actions.clearSearch}
                  className="pointer-events-auto rounded-full p-0.5 hover:bg-secondary-100"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setCustomerSearch('')}
                >
                  <TimesIcon className="h-5 w-5 fill-text-secondary" />
                </button>
              </div>
            )}
            {showSearchMenu && (
              <div
                id={suggestionsId}
                className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded border border-stroke bg-white py-1 shadow-md"
              >
                {suggestions.length > 0 && (
                  <div
                    role="listbox"
                    aria-label={MANUAL_CUSTOMERS_CONTENT.searchPlaceholder}
                    className="border-b border-stroke"
                  >
                    {suggestions.map((entity) => (
                      <button
                        key={entity.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="flex w-full flex-col items-start gap-0.5 px-4 py-1.5 text-left hover:bg-lightBlue-50"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelect(entity)}
                      >
                        <span className="text-base font-medium text-text-primary">{entity.name}</span>
                        <span className="text-base text-text-secondary">{formatQuoteAddress(entity)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="m-0 px-4 py-1.5 text-xs italic text-text-secondary">
                  {MANUAL_CUSTOMERS_CONTENT.searchMenu.prompt}
                </p>
                <button
                  type="button"
                  className="flex w-full items-center px-4 py-1.5 text-left text-lg font-semibold text-deepBlue-500 hover:bg-lightBlue-50"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commitTypedName()}
                >
                  <PlusIcon className="mr-3 h-6 w-6 fill-deepBlue-500" aria-hidden />
                  {`${MANUAL_CUSTOMERS_CONTENT.searchMenu.add} ${trimmedSearch}`}
                </button>
              </div>
            )}
          </div>
        </div>

        {showValueProps && <NoCustomersAddedState />}

        {formOpen && (
          <div className="space-y-4 rounded-xl bg-gray-50 p-4">
            {locks.name ? (
              <div className="flex flex-col gap-1">
                <ReadOnlyValue label={MANUAL_CUSTOMERS_CONTENT.fields.name.label} value={activeEntity?.name ?? ''} />
                <input type="hidden" {...register('name', { required: true })} />
                {isDuplicate && <HelperText variant="error">{MANUAL_CUSTOMERS_CONTENT.errors.duplicate}</HelperText>}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Label htmlFor="customerName" required>
                  {MANUAL_CUSTOMERS_CONTENT.fields.name.label}
                </Label>
                <TextInput
                  id="customerName"
                  className="w-full"
                  hasError={!!errors.name || isDuplicate}
                  {...register('name', {
                    required: MANUAL_CUSTOMERS_CONTENT.errors.name,
                    setValueAs: (value) => value.trim(),
                  })}
                />
                {errors.name?.message && <HelperText variant="error">{errors.name.message}</HelperText>}
                {isDuplicate && <HelperText variant="error">{MANUAL_CUSTOMERS_CONTENT.errors.duplicate}</HelperText>}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {locks.street ? (
                <ReadOnlyValue
                  label={MANUAL_CUSTOMERS_CONTENT.fields.street.label}
                  value={activeEntity?.street ?? ''}
                />
              ) : (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="customerStreet" required>
                    {MANUAL_CUSTOMERS_CONTENT.fields.street.label}
                  </Label>
                  <TextInput
                    id="customerStreet"
                    className="w-full"
                    hasError={!!errors.street}
                    {...register('street', {
                      required: MANUAL_CUSTOMERS_CONTENT.errors.street,
                      setValueAs: (value) => value.trim(),
                    })}
                  />
                  {errors.street?.message && <HelperText variant="error">{errors.street.message}</HelperText>}
                </div>
              )}
              {locks.street && <input type="hidden" {...register('street', { required: true })} />}

              {locks.city ? (
                <ReadOnlyValue label={MANUAL_CUSTOMERS_CONTENT.fields.city.label} value={activeEntity?.city ?? ''} />
              ) : (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="customerCity" required>
                    {MANUAL_CUSTOMERS_CONTENT.fields.city.label}
                  </Label>
                  <TextInput
                    id="customerCity"
                    className="w-full"
                    hasError={!!errors.city}
                    {...register('city', {
                      required: MANUAL_CUSTOMERS_CONTENT.errors.city,
                      setValueAs: (value) => value.trim(),
                    })}
                  />
                  {errors.city?.message && <HelperText variant="error">{errors.city.message}</HelperText>}
                </div>
              )}
              {locks.city && <input type="hidden" {...register('city', { required: true })} />}

              {locks.state ? (
                <ReadOnlyValue
                  label={MANUAL_CUSTOMERS_CONTENT.fields.state.label}
                  value={stateName(activeEntity?.state ?? '')}
                />
              ) : (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="customerState" required>
                    {MANUAL_CUSTOMERS_CONTENT.fields.state.label}
                  </Label>
                  <Controller
                    control={control}
                    name="state"
                    rules={{ required: MANUAL_CUSTOMERS_CONTENT.errors.state }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="customerState"
                          aria-label={MANUAL_CUSTOMERS_CONTENT.fields.state.label}
                          hasError={!!errors.state}
                        >
                          <SelectValue placeholder={MANUAL_CUSTOMERS_CONTENT.fields.state.placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(({ code, name }) => (
                            <SelectItem key={code} value={code}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.state?.message && <HelperText variant="error">{errors.state.message}</HelperText>}
                </div>
              )}
              {locks.state && <input type="hidden" {...register('state', { required: true })} />}

              {locks.zip ? (
                <ReadOnlyValue label={MANUAL_CUSTOMERS_CONTENT.fields.zip.label} value={activeEntity?.zip ?? ''} />
              ) : (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="customerZip" required>
                    {MANUAL_CUSTOMERS_CONTENT.fields.zip.label}
                  </Label>
                  <TextInput
                    id="customerZip"
                    className="w-full"
                    hasError={!!errors.zip}
                    {...register('zip', zipRules)}
                  />
                  {errors.zip?.message && <HelperText variant="error">{errors.zip.message}</HelperText>}
                </div>
              )}
              {locks.zip && <input type="hidden" {...register('zip', zipRules)} />}

              <ReadOnlyValue label={MANUAL_CUSTOMERS_CONTENT.fields.country.label} value={CUSTOMER_COUNTRY.name} />

              <div className="flex flex-col gap-1 lg:col-span-2">
                <Label htmlFor="customerVolume">{MANUAL_CUSTOMERS_CONTENT.fields.volume.label}</Label>
                <TextInput
                  id="customerVolume"
                  className="w-48"
                  hasError={!!errors.volume}
                  {...register('volume', {
                    setValueAs: (value) => value.trim(),
                    validate: (value) =>
                      value.trim() === '' || Number.isFinite(parseMonthlyInvoiceVolume(value))
                        ? true
                        : MANUAL_CUSTOMERS_CONTENT.errors.volume,
                  })}
                />
                {errors.volume?.message && <HelperText variant="error">{errors.volume.message}</HelperText>}
              </div>
            </div>

            <div className="border-t border-stroke" />

            <div className="flex justify-end">
              <Button variant="primary" disabled={!canSave} onClick={() => void saveCustomer()}>
                {MANUAL_CUSTOMERS_CONTENT.actions.save}
              </Button>
            </div>
          </div>
        )}

        {customers.length > 0 && (
          <div className="space-y-3">
            <h3 className="m-0 p-0 text-lg font-bold text-text-primary">
              {`${MANUAL_CUSTOMERS_CONTENT.addedCustomers.heading} (${customers.length})`}
            </h3>
            <div className="overflow-hidden rounded-xl border border-stroke">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{MANUAL_CUSTOMERS_CONTENT.addedCustomers.nameAndAddress}</TableHead>
                      <TableHead className="text-right">{MANUAL_CUSTOMERS_CONTENT.addedCustomers.volume}</TableHead>
                      <TableHead>
                        <span className="sr-only">{MANUAL_CUSTOMERS_CONTENT.addedCustomers.actions}</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customerKey(customer.name)}>
                        <TableCell className="whitespace-normal">
                          <p className="m-0 p-0 text-sm font-medium text-text-primary">{customer.name}</p>
                          <p className="m-0 p-0 text-xs text-text-secondary">{formatQuoteAddress(customer.address)}</p>
                        </TableCell>
                        <TableCell className="text-right">{formatSavedVolume(customer.volume, asCurrency)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <IconButton
                              className="bg-transparent"
                              icon={PencilIcon}
                              variant="ancillary"
                              name={`${MANUAL_CUSTOMERS_CONTENT.actions.edit} ${customer.name}`}
                              onClick={() => startEdit(customer)}
                            />
                            <div className="h-6 border-l border-stroke" aria-hidden />
                            <IconButton
                              className="bg-transparent"
                              icon={TrashCanIcon}
                              variant="destructive"
                              inverse
                              name={`${MANUAL_CUSTOMERS_CONTENT.actions.remove} ${customer.name}`}
                              onClick={() => setPendingDeleteKey(customerKey(customer.name))}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="m-0 mt-4 p-0 text-center text-xs text-text-secondary">{MANUAL_CUSTOMERS_CONTENT.disclaimer}</p>

      {pendingCustomer && (
        <RemoveCustomerModal
          customerName={pendingCustomer.name}
          onClose={() => setPendingDeleteKey(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};
